/* @flow */
import { Observable } from 'rxjs';
import { combineEpics } from 'redux-observable';
import type { ActionsObservable } from 'redux-observable';
import { querystring } from 'trustar/utils';
import createDebugger from 'debug';

const debug = createDebugger('app:modules:profile:epics'); // eslint-disable-line no-unused-vars

import {
  UPDATE,
  UPDATE_SUCCESS,
  UPDATE_FAILURE,
  UPDATE_PREFERENCES,
  UPDATE_PREFERENCES_SUCCESS,
  UPDATE_PREFERENCES_FAILURE,
  FETCH,
  FETCH_SUCCESS,
  FETCH_FAILURE,
  FETCH_QUOTAS,
  FETCH_QUOTAS_SUCCESS,
  FETCH_QUOTAS_FAILURE,
  SAVE_PASSWORD,
  SAVE_PASSWORD_SUCCESS,
  SAVE_PASSWORD_FAILURE,
} from './action-types.js';
import { updatePreferences } from './actions';
import { PROFILE_ENDPOINT, PREFERENCES_ENDPOINT, PROFILE_QUOTAS_ENDPOINT } from './types.js';
import * as getters from './getters.js';
import { success, error } from 'modules/alerts/actions.js';
import { setThemeFromPreferences } from 'modules/app/actions.js';
import { theme as getTheme } from 'modules/app/getters';
import { LIGHT_THEME, NO_THEME } from 'trustar-2.0/themes/common/constants';

export const fetchEpic = (action$: ActionsObservable<*>, store: Store, { ajax }: Object) =>
  action$.ofType(FETCH)
    .mergeMap(() =>
      ajax.get(PROFILE_ENDPOINT)
        .mergeMap(({ response }) => {
          const successAction = {
            type: FETCH_SUCCESS,
            payload: response,
          };

          if (response.preferences && response.preferences.themeMode) {
            return Observable.from([
              setThemeFromPreferences(response.preferences.themeMode),
              successAction,
            ]);
          } else {
            // If the user doesn't have a theme selected, we check if they've got something in the localStorage by checking the Redux's store.
            // If they still have nothing, we default to the Light theme and update their preferences.
            const storedTheme = getTheme(store.getState());
            const themeMode = storedTheme && storedTheme !== NO_THEME ? storedTheme : LIGHT_THEME;

            return Observable.from([
              updatePreferences({ themeMode: themeMode.toUpperCase() }),
              successAction,
            ]);
          }
        })
        .catch(err => Observable.of({
          type: FETCH_FAILURE,
          error: true,
          payload: err,
          meta: {
            alert: { type: 'error', text: 'There was an error fetching your settings' },
          },
        }))
    );

export const fetchQuotasEpic = (action$: ActionsObservable<*>, store: Store, { ajax }: Object) =>
  action$.ofType(FETCH_QUOTAS)
    .mergeMap(() =>
      ajax.get(PROFILE_QUOTAS_ENDPOINT)
        .map(({ response }) => ({
          type: FETCH_QUOTAS_SUCCESS,
          payload: response,
        }))
        .catch(err => Observable.of({
          type: FETCH_QUOTAS_FAILURE,
          error: true,
          payload: err,
          meta: {
            alert: { type: 'error', text: 'There was an error fetching your quota settings' },
          },
        })));

const updateEpic = (action$, store, { ajax }) =>
  action$.ofType(UPDATE)
    .switchMap((action) => {
      const profile = getters.profile(store.getState());
      let payload = profile
        .merge(action.payload)
        .update('company', c => c.delete('enclaves'));

      if (!profile.get('id')) {
        throw new Error('No user ID found.');
      }

      const url = `/services/users/${profile.get('guid')}`;
      return ajax.postJSON(url, payload)
        .map(({ response }) => ({
          type: UPDATE_SUCCESS,
          payload: response,
          meta: {
            alert: { type: 'success', text: 'User updated successfully.' },
          }
        }))
        .catch(err => {
          const message = err.status === 400
            ? 'User with this email already exists.'
            : 'User could not be updated';
          return Observable.of({
            type: UPDATE_FAILURE,
            payload: err,
            error: true,
            meta: {
              alert: { type: 'error', text: message },
            }
          });
        });
    });

const updatePreferencesEpic = (action$, store, { ajax }) =>
  action$.ofType(UPDATE_PREFERENCES)
    .mergeMap(({ payload }) => {
      return ajax.postJSON(PREFERENCES_ENDPOINT, payload)
        .mergeMap(({ response }) => {
          return [
            { type: UPDATE_PREFERENCES_SUCCESS, payload: response },
            success('User preferences updated successfully.')
          ];
        })
        .catch(err => {
          return Observable.from([
            { type: UPDATE_PREFERENCES_FAILURE },
            error('User preferences could not be updated. Please reload this page and try again.')
          ]);
        });
    });

const updateThemeOptionsFromPreferences = (action$) =>
  action$.ofType(UPDATE_PREFERENCES)
    .pluck('payload', 'themeMode')
    .filter((themeMode) => !!themeMode)
    .map((themeMode) => setThemeFromPreferences(themeMode));

const updatePasswordEpic = (action$, store, { ajax }) =>
  action$.ofType(SAVE_PASSWORD)
    .mergeMap(({ payload }) => {
      return ajax.postJSON(`${PROFILE_ENDPOINT}/password`, payload)
        .mergeMap(({ response }) => {
          return [
            { type: SAVE_PASSWORD_SUCCESS, payload: response },
            success('Password changed successfully.'),
          ];
        })
        .catch(err => {
          const actions = [
            { type: SAVE_PASSWORD_FAILURE }
          ];
          if (err.status === 403) {
            actions.push(error('The current password you entered is incorrect.'));
          } else {
            actions.push(error('Password could not be saved. Please reload this page and try again.'));
          }
          return Observable.from(actions);
        });
    });


export default combineEpics(
  fetchEpic,
  fetchQuotasEpic,
  updateEpic,
  updatePreferencesEpic,
  updatePasswordEpic,
  updateThemeOptionsFromPreferences,
);



// WEBPACK FOOTER //
// ./src/app/modules/profile/epics.js